import Metadata from '../components/Metadata';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import ImageGallery from '../components/ImageGallery';
import ServiceCard from '../components/ServiceCard';
import useCmsCollection from '../hooks/useCmsCollection';
import { serviceCards } from '../data/siteData';
import { normalizeGalleryItems } from '../lib/gallery';
import { assetUrl } from '../lib/media';

export default function ServicePage({ config }) {
  const galleryQuery = useCmsCollection('galleri', {
    endpoint: 'v2',
    limit: 6,
    offset: 0,
    sort: 'updatedAt',
    order: 'desc',
    term: [`kategori:${config.gallerySlug}`],
  });

  const gallery = normalizeGalleryItems(galleryQuery.items);
  const otherServices = serviceCards.filter((service) => service.slug !== config.slug).slice(0, 3);

  return (
    <>
      <Metadata
        title={config.meta.title}
        description={config.meta.description}
        image={assetUrl(config.meta.ogImage, 1200)}
        path={`/ydelser/${config.slug}`}
      />

      <PageHero
        eyebrow={`Ydelse · ${config.label}`}
        title={config.title}
        description={config.intro}
        image={config.heroImage}
        primaryAction={{ label: 'Få et tilbud', to: config.ctaPath }}
        secondaryAction={{ label: 'Ring 42 30 79 39', href: 'tel:+4542307939' }}
      />

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_0.8fr] lg:px-8">
          <div>
            <SectionHeading
              eyebrow="Om ydelsen"
              title={`Professionel ${config.label.toLowerCase()} med fokus på kvalitet`}
              description="Vi tilpasser løsningen til huset, materialerne og dine ønsker, så resultatet både er funktionelt og visuelt stærkt."
            />

            <ul className="mt-8 space-y-4 border-l-2 border-brand-orange pl-5 text-sm leading-7 text-brand-gray-700 sm:text-base">
              {config.points.map((point) => (
                <li key={point} className="relative">
                  <span className="absolute -left-[1.55rem] top-2 h-2.5 w-2.5 rounded-full bg-brand-orange" aria-hidden="true" />
                  <span className="font-semibold text-brand-black">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <article className="border border-brand-gray-200 bg-brand-black p-6 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-orange">Hvad du kan forvente</p>
            <ul className="mt-5 space-y-4 text-sm leading-7 text-brand-gray-200">
              <li>• Klar dialog om løsning og tidsplan</li>
              <li>• Materialer valgt til det danske klima</li>
              <li>• Et ordentligt og ryddeligt arbejdsforløb</li>
              <li>• En finish der matcher resten af boligen</li>
            </ul>
            <div className="mt-8">
              <p className="text-sm leading-7 text-brand-gray-200">
                Har du brug for rådgivning før du beslutter dig, tager vi gerne en snak om mulighederne.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className="border-y border-brand-gray-200 bg-brand-gray-50 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Galleri"
            title={`Se billeder fra ${config.label.toLowerCase()}`}
            description="Galleriet er hentet direkte via CMS på slug-reference, så billederne altid følger den relevante service."
          />

          <div className="mt-10">
            {galleryQuery.loading ? (
              <div className="border border-brand-gray-200 bg-white p-6 text-sm text-brand-gray-600">
                Henter galleri...
              </div>
            ) : gallery.length ? (
              <ImageGallery items={gallery} columnsClass="lg:grid-cols-2" />
            ) : (
              <div className="border border-brand-gray-200 bg-white p-6 text-sm text-brand-gray-600">
                Der er endnu ikke indhold i galleriet for denne ydelse.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Andre ydelser"
            title="Se også de øvrige løsninger fra RS Byg"
            description="Det er let at skifte mellem servicesiderne, hvis du vil sammenligne løsningerne."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {otherServices.map((service) => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-black py-20 text-white sm:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-orange">Næste skridt</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {config.cta}
          </h2>
          <p className="mt-5 text-sm leading-7 text-brand-gray-200">
            Kontakt RS Byg i dag, så finder vi den løsning der passer bedst til dit projekt.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a href="/kontakt" className="inline-flex items-center justify-center bg-brand-orange px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-orange-hover">
              {config.cta}
            </a>
            <a href="tel:+4542307939" className="inline-flex items-center justify-center border border-white/20 px-6 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange whitespace-nowrap">
              Ring 42 30 79 39
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
